// src/db/seed.ts
import { db } from './db';
import type {
  Branch,
  AdminCashBox,
  InvoiceResolution,
  CostCenter,
  AccountingEntry,
} from './db';

// ─────────────────────────────────────────────────────────────
// CLAVES LEGACY EN localStorage (migración idempotente v109)
// ─────────────────────────────────────────────────────────────
const LEGACY_KEYS = {
  COST_CENTERS: 'niiz_cost_centers_v1',
  ACCOUNTING_ENTRIES: 'niiz_accounting_entries_v1',
  CASH_BOXES: 'niiz_admin_cash_boxes_v1',
  BRANCHES: 'niiz_branches_v1',
  INVOICE_RESOLUTIONS: 'niiz_invoice_resolutions_v1',
} as const;

// ─────────────────────────────────────────────────────────────
// CONFIGURACIÓN NORMATIVA DE LAS 4 CAJAS MAESTRAS (50/20/15/15)
// ─────────────────────────────────────────────────────────────
const MASTER_BOX_BLUEPRINT: ReadonlyArray<{
  role: AdminCashBox['role'];
  type: AdminCashBox['type'];
  name: string;
  percentage: number;
  canBeDonor: boolean;
  tags: string[];
}> = [
  {
    role: 'OPERATING',
    type: 'MASTER',
    name: 'OPERACION',
    percentage: 50,
    canBeDonor: true,
    tags: ['master', 'operacion', 'normativa'],
  },
  {
    role: 'REINVESTMENT',
    type: 'MASTER',
    name: 'REINVERSION',
    percentage: 20,
    canBeDonor: true,
    tags: ['master', 'reinversion', 'normativa'],
  },
  {
    role: 'TAX',
    type: 'MASTER',
    name: 'IMPUESTOS',
    percentage: 15,
    canBeDonor: false, // Jerarquía sagrada: nunca donante
    tags: ['master', 'impuestos', 'normativa', 'sagrada'],
  },
  {
    role: 'PROFIT',
    type: 'MASTER',
    name: 'GANANCIAS',
    percentage: 15,
    canBeDonor: true,
    tags: ['master', 'ganancias', 'normativa'],
  },
];

// ─────────────────────────────────────────────────────────────
// UTILIDADES INTERNAS
// ─────────────────────────────────────────────────────────────
const nowIso = (): string => new Date().toISOString();
const genId = (prefix: string): string =>
  `${prefix}_${crypto.randomUUID()}`;

/**
 * Lee y parsea de forma segura una clave de localStorage.
 * Devuelve `null` si no existe o si el JSON es inválido.
 */
function readLegacyJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/**
 * Elimina una clave legacy de localStorage de forma segura.
 * Solo se ejecuta DESPUÉS de una migración exitosa.
 */
function removeLegacyKey(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Silencioso: la clave simplemente no existe o no se puede tocar
  }
}

/**
 * Determina la moneda activa del negocio de forma agnóstica.
 * NO fuerza ninguna moneda fija: si no hay configuración, devuelve
 * `undefined` para que cada tabla lo trate como opcional.
 */
async function resolveActiveCurrency(): Promise<string | undefined> {
  try {
    const settings = await db.settings.toCollection().first();
    const code = settings?.currency_code?.trim();
    if (code && code.length >= 2 && code.length <= 4) return code.toUpperCase();
    const symbol = settings?.currency_symbol?.trim();
    if (symbol && symbol.length >= 1 && symbol.length <= 4) return symbol;
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Resuelve el business_id activo de la sesión.
 */
async function resolveBusinessId(): Promise<string> {
  try {
    const settings = await db.settings.toCollection().first();
    const bid = settings?.business_id?.trim();
    return bid || 'local';
  } catch {
    return 'local';
  }
}

// ─────────────────────────────────────────────────────────────
// AUTO-SANACIÓN 1: SEDE PRINCIPAL POR DEFECTO
// ─────────────────────────────────────────────────────────────
async function ensureMainBranch(
  businessId: string,
  report: SeedReport,
): Promise<void> {
  const existingMain = await db.branches
    .where('business_id')
    .equals(businessId)
    .and((b) => b.is_main === true)
    .first();

  if (existingMain) {
    report.branches.existed += 1;
    return;
  }

  const branch: Branch = {
    id: genId('br'),
    business_id: businessId,
    name: 'Sede Principal / Global',
    code: 'main',
    is_main: true,
    is_active: true,
    created_at: nowIso(),
    tags: ['sede-principal', 'default', 'auto-sanacion'],
  };

  await db.branches.add(branch);
  report.branches.created += 1;
}

/**
 * Garantiza que exista al menos una sede principal.
 * Si hay múltiples sedes pero ninguna es `is_main`, marca la más antigua.
 */
async function healOrphanBranches(
  businessId: string,
  report: SeedReport,
): Promise<void> {
  const allBranches = await db.branches
    .where('business_id')
    .equals(businessId)
    .toArray();

  if (allBranches.length === 0) return;

  const hasMain = allBranches.some((b) => b.is_main === true);
  if (hasMain) return;

  const sorted = [...allBranches].sort((a, b) =>
    String(a.created_at || '').localeCompare(String(b.created_at || '')),
  );
  const oldest = sorted[0];
  await db.branches.update(oldest.id, { is_main: true });
  report.branches.healed += 1;
}

// ─────────────────────────────────────────────────────────────
// AUTO-SANACIÓN 2: LAS 4 CAJAS MAESTRAS NORMATIVAS
// ─────────────────────────────────────────────────────────────
async function ensureMasterBoxes(
  businessId: string,
  currency: string | undefined,
  report: SeedReport,
): Promise<void> {
  const existingMasters = await db.admin_cash_boxes
    .where('business_id')
    .equals(businessId)
    .and((b) => b.type === 'MASTER')
    .toArray();

  const existingByRole = new Map(
    existingMasters.map((b) => [b.role, b]),
  );

  for (const blueprint of MASTER_BOX_BLUEPRINT) {
    const existing = existingByRole.get(blueprint.role);
    if (existing) {
      report.cashBoxes.existed += 1;
      // Curación suave: si el porcentaje fue alterado, NO se sobreescribe
      // (solo se repara si el registro está dañado estructuralmente).
      if (
        typeof existing.percentage !== 'number' ||
        Number.isNaN(existing.percentage) ||
        typeof existing.balance !== 'number' ||
        Number.isNaN(existing.balance)
      ) {
        await db.admin_cash_boxes.update(existing.id, {
          percentage: blueprint.percentage,
          balance: 0,
        });
        report.cashBoxes.healed += 1;
      }
      continue;
    }

    const box: AdminCashBox = {
      id: genId('box'),
      business_id: businessId,
      type: blueprint.type,
      role: blueprint.role,
      name: blueprint.name,
      percentage: blueprint.percentage,
      balance: 0,
      floor: 0,
      can_donate_to: [],
      tags: blueprint.tags,
      status: 'ACTIVE',
      created_at: nowIso(),
      // `currency` es opcional en AdminCashBox: solo se escribe si existe.
      ...(currency == null ? {} : { currency }),
    };

    await db.admin_cash_boxes.add(box);
    report.cashBoxes.created += 1;
  }

  // Validación de integridad: la suma de las MASTER debe ser 100%.
  const masters = await db.admin_cash_boxes
    .where('business_id')
    .equals(businessId)
    .and((b) => b.type === 'MASTER')
    .toArray();

  const sumPercentage = masters.reduce((acc, m) => acc + (m.percentage || 0), 0);
  if (sumPercentage !== 100) {
    report.warnings.push(
      `⚠️ [SEED] La suma de las cajas maestras es ${sumPercentage}% (esperado 100%). Requiere calibración manual.`,
    );
  }
}

// ─────────────────────────────────────────────────────────────
// AUTO-SANACIÓN 3: RESOLUCIÓN DE FACTURACIÓN POR DEFECTO
// ─────────────────────────────────────────────────────────────
async function ensureDefaultInvoiceResolution(
  businessId: string,
  mainBranchId: string | undefined,
  report: SeedReport,
): Promise<void> {
  const existing = await db.invoice_resolutions
    .where('business_id')
    .equals(businessId)
    .and((r) => r.prefix === 'FAC' && r.is_active === true)
    .first();

  if (existing) {
    report.invoiceResolutions.existed += 1;
    return;
  }

  const today = new Date();
  const validUntil = new Date(today);
  validUntil.setFullYear(validUntil.getFullYear() + 2); // Vigencia: 2 años

  const resolution: InvoiceResolution = {
    id: genId('res'),
    business_id: businessId,
    prefix: 'FAC',
    from_number: 1,
    to_number: 10000,
    current_number: 0,
    valid_from: today.toISOString().slice(0, 10),
    valid_until: validUntil.toISOString().slice(0, 10),
    is_active: true,
    tags: ['facturacion', 'default', 'auto-sanacion'],
    created_at: nowIso(),
    // `branch_id` es opcional: solo se escribe con la sede principal resuelta.
    ...(mainBranchId == null ? {} : { branch_id: mainBranchId }),
  };

  await db.invoice_resolutions.add(resolution);
  report.invoiceResolutions.created += 1;
}

// ─────────────────────────────────────────────────────────────
// MIGRACIÓN IDEMPOTENTE DESDE localStorage
// ─────────────────────────────────────────────────────────────
interface LegacyCostCenterRow {
  id?: string;
  name?: string;
  code?: string;
  budget?: number;
  branch_id?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

interface LegacyAccountingEntryRow {
  id?: string;
  number?: string;
  date?: string;
  kind?: string;
  status?: string;
  description?: string;
  amount?: number;
  idempotency_key?: string;
  tags?: string[];
  [key: string]: unknown;
}

/**
 * Migra centros de costo legacy a Dexie v109 sin duplicar.
 * La idempotencia se garantiza por el campo `id`: si ya existe, se ignora.
 */
async function migrateLegacyCostCenters(
  businessId: string,
  mainBranchId: string | undefined,
  currency: string | undefined,
  report: SeedReport,
): Promise<void> {
  const legacyRows = readLegacyJson<LegacyCostCenterRow[]>(LEGACY_KEYS.COST_CENTERS);
  if (!Array.isArray(legacyRows) || legacyRows.length === 0) return;

  const existingIds = new Set(
    (
      await db.cost_centers
        .where('business_id')
        .equals(businessId)
        .toArray()
    ).map((c) => c.id),
  );

  const toInsert: CostCenter[] = [];
  for (const row of legacyRows) {
    const id = row.id?.trim() || `cc_legacy_${row.name || 'sinnombre'}_${row.code || 'sincode'}`;
    if (existingIds.has(id)) {
      report.migrations.costCenters.skipped += 1;
      continue;
    }

    toInsert.push({
      id,
      business_id: businessId,
      branch_id: (row.branch_id as string) || mainBranchId || '',
      code: (row.code as string) || 'LEGACY',
      name: (row.name as string) || 'Centro de Costo Legacy',
      budget: typeof row.budget === 'number' ? row.budget : 0,
      tags: ['legacy', 'migrado', 'v109'],
      is_active: row.is_active !== false,
      created_at: nowIso(),
      // `currency` es opcional en CostCenter: solo se escribe si existe.
      ...(currency == null ? {} : { currency }),
    });
    existingIds.add(id);
    report.migrations.costCenters.migrated += 1;
  }

  if (toInsert.length > 0) {
    await db.cost_centers.bulkPut(toInsert);
  }

  // Solo se elimina la clave legacy si la migración fue exitosa
  removeLegacyKey(LEGACY_KEYS.COST_CENTERS);
}

/**
 * Migra asientos contables legacy a Dexie v109 sin duplicar.
 * La idempotencia se garantiza por `idempotency_key`.
 */
async function migrateLegacyAccountingEntries(
  businessId: string,
  currency: string | undefined,
  report: SeedReport,
): Promise<void> {
  const legacyRows = readLegacyJson<LegacyAccountingEntryRow[]>(
    LEGACY_KEYS.ACCOUNTING_ENTRIES,
  );
  if (!Array.isArray(legacyRows) || legacyRows.length === 0) return;

  const existingKeys = new Set(
    (
      await db.accounting_entries
        .where('business_id')
        .equals(businessId)
        .toArray()
    ).map((e) => e.idempotency_key),
  );

  const toInsert: AccountingEntry[] = [];
  for (const row of legacyRows) {
    const idempotencyKey =
      row.idempotency_key?.trim() ||
      `legacy_${row.id || row.number || 'asiento'}_${row.date || 'sinfecha'}`;

    if (existingKeys.has(idempotencyKey)) {
      report.migrations.accountingEntries.skipped += 1;
      continue;
    }

    const dateStr = row.date?.slice(0, 10) || new Date().toISOString().slice(0, 10);
    // `postings_json` NO forma parte de `LegacyAccountingEntryRow` (llega por el
    // índice de firma como `unknown`): se lee con guarda explícita.
    const legacyPostings: unknown = row['postings_json'];

    toInsert.push({
      id: genId('ae'),
      business_id: businessId,
      number: row.number?.trim() || `LEG-${Date.now()}`,
      date: dateStr,
      posted_at: nowIso(),
      kind: (row.kind as AccountingEntry['kind']) || 'ADJUSTMENT',
      postings_json: JSON.stringify(Array.isArray(legacyPostings) ? legacyPostings : []),
      description: row.description || 'Asiento migrado desde localStorage',
      status: (row.status as AccountingEntry['status']) || 'POSTED',
      caused_by: [],
      idempotency_key: idempotencyKey,
      source_json: JSON.stringify({ legacy: true, origin: LEGACY_KEYS.ACCOUNTING_ENTRIES }),
      prev_hash: '',
      hash: '',
      seq: 0, // Se recalcula en el próximo asiento real
      tags: Array.isArray(row.tags) ? [...row.tags, 'legacy', 'migrado'] : ['legacy', 'migrado'],
      created_at: nowIso(),
      created_by: 'seed-migration',
      // `currency` es opcional en AccountingEntry.
      ...(currency == null ? {} : { currency }),
    });
    existingKeys.add(idempotencyKey);
    report.migrations.accountingEntries.migrated += 1;
  }

  if (toInsert.length > 0) {
    await db.accounting_entries.bulkPut(toInsert);
  }

  removeLegacyKey(LEGACY_KEYS.ACCOUNTING_ENTRIES);
}

// ─────────────────────────────────────────────────────────────
// REPORTE DE LA EJECUCIÓN DEL SEED
// ─────────────────────────────────────────────────────────────
export interface SeedReport {
  businessId: string;
  currency: string | undefined;
  branches: { created: number; existed: number; healed: number };
  cashBoxes: { created: number; existed: number; healed: number };
  invoiceResolutions: { created: number; existed: number };
  migrations: {
    costCenters: { migrated: number; skipped: number };
    accountingEntries: { migrated: number; skipped: number };
  };
  warnings: string[];
  executedAt: string;
}

// ─────────────────────────────────────────────────────────────
// FUNCIÓN PRINCIPAL DE INICIALIZACIÓN Y AUTO-SANACIÓN v109
// ─────────────────────────────────────────────────────────────
export const seedDatabase = async (): Promise<SeedReport> => {
  const report: SeedReport = {
    businessId: 'local',
    currency: undefined,
    branches: { created: 0, existed: 0, healed: 0 },
    cashBoxes: { created: 0, existed: 0, healed: 0 },
    invoiceResolutions: { created: 0, existed: 0 },
    migrations: {
      costCenters: { migrated: 0, skipped: 0 },
      accountingEntries: { migrated: 0, skipped: 0 },
    },
    warnings: [],
    executedAt: nowIso(),
  };

  try {
    // 1) Asegurar que la base de datos esté abierta
    if (!db.isOpen()) {
      await db.open();
    }

    // 2) Resolver contexto del negocio (agnóstico de moneda)
    const businessId = await resolveBusinessId();
    const currency = await resolveActiveCurrency();
    report.businessId = businessId;
    report.currency = currency;

    // 3) Auto-sanación estructural dentro de UNA transacción atómica
    let mainBranchId: string | undefined;

    await db.transaction(
      'rw',
      [db.branches, db.admin_cash_boxes, db.invoice_resolutions, db.cost_centers, db.accounting_entries],
      async () => {
        // 3.1) Sede principal
        await ensureMainBranch(businessId, report);
        await healOrphanBranches(businessId, report);

        const mainBranch = await db.branches
          .where('business_id')
          .equals(businessId)
          .and((b) => b.is_main === true)
          .first();
        mainBranchId = mainBranch?.id;

        // 3.2) Las 4 cajas maestras normativas (50/20/15/15)
        await ensureMasterBoxes(businessId, currency, report);

        // 3.3) Resolución de facturación por defecto
        await ensureDefaultInvoiceResolution(businessId, mainBranchId, report);

        // 3.4) Migraciones idempotentes desde localStorage
        await migrateLegacyCostCenters(businessId, mainBranchId, currency, report);
        await migrateLegacyAccountingEntries(businessId, currency, report);
      },
    );

    // 4) Log consolidado
    console.log(
      '✅ [SEED v109] Auto-sanación completada:',
      JSON.stringify(
        {
          businessId: report.businessId,
          currency: report.currency || 'sin-configurar',
          branches: report.branches,
          cashBoxes: report.cashBoxes,
          invoiceResolutions: report.invoiceResolutions,
          migrations: report.migrations,
        },
        null,
        2,
      ),
    );

    if (report.warnings.length > 0) {
      console.warn('⚠️ [SEED v109] Advertencias:', report.warnings);
    }

    return report;
  } catch (error: any) {
    console.error('❌ [SEED v109] Error crítico en la auto-sanación:', error);
    report.warnings.push(
      `❌ Error crítico: ${error?.message || 'Error desconocido en seedDatabase'}`,
    );
    return report;
  }
};

export default seedDatabase;